/**
 * Visits panel, Step A (labels-v2 PROPOSAL.md, founder-approved 2026-10-12).
 *
 * The panel shows WHO is visiting and what they did. Identity is a quiet
 * supporting mark, never the headline:
 *
 *   - Confirmed: a small mint shield after the name. Hover shows one line
 *     of proof (`identityState().evidence[0]`).
 *   - Mismatch: a coral shield, the name in quotes, a generic coral Bot
 *     glyph (never the impersonated brand) and a coral left edge. Hover
 *     shows the reason sentence (labels-v2 ux.md §3.2).
 *   - Everything else (no verdict, unvetted signer, legacy rows, people):
 *     nothing at all. There is no word for the default state.
 *
 * Security (non-negotiable): the state comes ONLY from `identityState()`
 * (`@sill/audit`), which reads server-computed `identity_verification` /
 * `impersonation_reason`. Nothing here reads `cf_verified_bot`, a client
 * spoof heuristic, or the audit envelope signature.
 *
 * Brand logos stay on every non-Mismatch row for the CLAIMED name (founder
 * decision 2026-10-12; extended to Sill's own tools 2026-10-13): a logo
 * means "says it is", like caller ID. Nothing says confirmed without
 * server evidence.
 *
 * Pure: no React. Callers pass `identityState(record)` in; rendering lives
 * in `VisitIdentityMarks.tsx`.
 */

import { humanRecordDevice, humanRecordName, identityState, isMeaningfulProbing, isNamedSenderGroupKey, uaGroupToken, uaGroupTokenKey, visitCategoryForToken, type IdentityStateResult } from '@sill/audit';
import type { AgentRegistryCategory, AuditRecord, SecurityFlag } from '@sill/types';
import { BUNDLED_REGISTRY, SILL_INTERNAL_TOOLS, isProbePath, probeKindOf, isSillFirstPartySigner, isSillInternalUserAgent } from '@sill/types';

import { inferAgentFromUserAgent, recordHeadline } from './intent-label.js';

export type { IdentityStateResult } from '@sill/audit';

/** The only two marks the visits panel draws. `null` = draw nothing. */
export type VisitMark = 'confirmed' | 'mismatch' | null;

export function visitMark(result: IdentityStateResult): VisitMark {
  if (result.state === 'confirmed') return 'confirmed';
  if (result.state === 'mismatch') return 'mismatch';
  return null;
}

function nonEmpty(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;
}

function sentence(s: string): string {
  const t = s.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/**
 * The one line of proof behind a Confirmed mark, e.g. "Address is on
 * OpenAI's published list." `null` unless the server confirmed the visit.
 */
export function confirmedProof(result: IdentityStateResult): string | null {
  if (result.state !== 'confirmed') return null;
  const first = result.evidence[0];
  return first !== undefined ? sentence(first) : null;
}

/**
 * Plain-language reason for a Mismatch (labels-v2 ux.md §3.2). `null`
 * unless the server recorded an `impersonation_reason`.
 */
export function mismatchReason(record: AuditRecord, result: IdentityStateResult): string | null {
  if (result.state !== 'mismatch' || result.reason === undefined) return null;
  const opName = nonEmpty(record.identity_evidence?.operator_display);
  const op = opName ?? 'the company';
  const opPossessive = opName !== undefined ? `${opName}'s` : "the company's";
  switch (result.reason) {
    case 'ip_outside_published_ranges':
      return `Uses ${opPossessive} crawler name from an address ${op} doesn't publish.`;
    case 'rdns_mismatch':
      return `Uses ${opPossessive} crawler name, but its address doesn't trace back to ${op}.`;
    case 'signer_mismatch': {
      const rawHost = nonEmpty(record.identity_evidence?.signer_host) ?? nonEmpty(record.signed_agent);
      // Visitor-supplied string: bound its length (security review Step A n2).
      const host = rawHost !== undefined && rawHost.length > 64 ? `${rawHost.slice(0, 64)}…` : rawHost;
      return host !== undefined
        ? `Signed by ${host}, which ${op} doesn't use.`
        : `Signed by someone ${op} doesn't use.`;
    }
    case 'sig_invalid':
      return "Sent a signature that failed Sill's check.";
    case 'robots_token_ua':
      return `Uses a name ${op} only puts in robots.txt, never on real visits.`;
    default:
      return null;
  }
}

/** Mismatch callout copy at the top of Overview.
 *
 * Founder bug 2026-10-18: the old sentence pointed the merchant at
 * their firewall, but Sill IS the firewall when a Worker install is in
 * path (ADR-0245). The Block ▾ in the panel header blocks the
 * impostor's addresses directly; the real crawler keeps working
 * because verified-crawler addresses are screened out of blocklist
 * writes server-side. */
export function mismatchCallout(record: AuditRecord, result: IdentityStateResult): string | null {
  if (result.state !== 'mismatch') return null;
  const op = nonEmpty(record.identity_evidence?.operator_display) ?? result.name;
  return `Block the impostor's addresses — real ${op} visits keep working.`;
}

/** Mismatch names are shown in quotes: it is a claim we disproved. */
export function quotedName(name: string): string {
  return `“${name}”`;
}

// ---------------------------------------------------------------------------
// Flagged + unverified presentation (founder bug 2026-10-20)
// ---------------------------------------------------------------------------
//
// When Security has flagged this sender on this site AND Sill did not
// confirm the identity (no server verdict in {`signature`, `published_ip`,
// `rdns`}), a self-declared UA name must not get a reassuring presentation
// in Activity. The row renders:
//
//   "Unknown bot · says it is <token>"
//
// with the generic Bot glyph (not the claimed brand's logo) and no AI
// "who" description. Verified agents keep their name, logo and
// description — a confirmed identity, flagged on this site, STILL
// renders normally and the chip + the Confirmed mark coexist.

/** The record carried a verified identity (ADR-0229) — a Confirmed row. */
function hasVerifiedIdentity(record: AuditRecord): boolean {
  const v = record.identity_verification;
  return v === 'signature' || v === 'published_ip' || v === 'rdns';
}

/**
 * True when the row is flagged by Security but not verified by Sill —
 * i.e. the UA name is self-declared and someone else has already flagged
 * this sender. Drives the "says it is <token>" presentation + the
 * registry-logo + AI-who suppression.
 */
export function isUnverifiedFlagged(record: AuditRecord): boolean {
  const flag = record.security_flag;
  if (flag === undefined) return false;
  return !hasVerifiedIdentity(record);
}

/**
 * The name an UNVERIFIED, FLAGGED row shows: "Unknown bot · says it is
 * <token>" — never the claimed brand name standing on its own. Returns
 * `null` when the UA carries no usable product token.
 */
export function unverifiedFlaggedName(record: AuditRecord): string | null {
  const tok = uaGroupToken(record.user_agent);
  if (tok === null) return null;
  return `Unknown bot · says it is ${tok}`;
}

/**
 * The staff-visible registry category for a row — read ONLY from the
 * SERVER agent id (never the UA's claim). Returns `null` when no server
 * match. Used by the detail header's description to swap in a short
 * neutral phrase ("Vulnerability scanner") when the registry has
 * already classified the agent as hostile (so a scanner never surfaces
 * a benign AI-written "who").
 */
export function registryCategoryForRecord(record: AuditRecord): AgentRegistryCategory | null {
  const id = record.identification_match?.agent_id;
  if (typeof id !== 'string' || id.length === 0) return null;
  return SEED_BY_AGENT_ID.get(id)?.category ?? null;
}

/** `null` unless the record is in the registry 'hostile' category. */
export function hostileRegistryPurpose(record: AuditRecord): string | null {
  return registryCategoryForRecord(record) === 'hostile' ? 'Vulnerability scanner' : null;
}

export type { SecurityFlag };

/**
 * The name a list row shows. Mismatch: the claimed token in quotes.
 * Otherwise the existing record headline, minus the "· signed by …"
 * suffix the shared headline adds for an unvetted / unregistered signer —
 * signer detail lives only in Raw data (labels-v2 ux.md §3.3). The kept
 * part is the UA product token (what the visitor says it is), never a
 * registry display name, so the ADR-0234 MAJOR 1 guard still holds.
 */
export function visitListName(headline: string, result: IdentityStateResult): string {
  if (result.state === 'mismatch') return quotedName(result.name);
  const suffixed = /^(.+?) · signed by .+$/.exec(headline);
  if (suffixed !== null && suffixed[1] !== undefined) return suffixed[1];
  if (/^Signed by /.test(headline)) return result.name;
  return headline;
}

// ---------------------------------------------------------------------------
// Logo rule (one function for list, header and feed)
// ---------------------------------------------------------------------------

export type VisitLogo =
  | { readonly kind: 'rejected' }
  | { readonly kind: 'mismatch' }
  | { readonly kind: 'sill' }
  | { readonly kind: 'brand'; readonly agent_id: string | null };

/**
 * THE switch for the Sill mark (security review M2).
 *   true  — the mark shows for any visit that CLAIMS to be a Sill tool
 *           (`isSillToolVisit`), like every other brand logo;
 *   false — the mark shows only when Sill's own signature confirmed the
 *           visit; an unconfirmed "Sill" gets the generic bot glyph.
 * Founder decision 2026-10-13 ("i need to see Sill logo and the other
 * vendors as well"): `true`, reversing the 2026-10-12 confirmed-only
 * choice. A logo is caller ID, not proof: the ✓ Confirmed mark still comes
 * ONLY from the server verdict (`identityState()`), and a Mismatch still
 * gets the coral bot glyph, never the Sill mark. One line to flip;
 * `visitLogo` and `AgentLogo` both read it.
 */
export const SILL_MARK_FOR_CLAIMS: boolean = true;

/** May this visit carry the Sill mark under `SILL_MARK_FOR_CLAIMS`? */
export function showsSillMark(record: AuditRecord, result: IdentityStateResult): boolean {
  if (!isSillToolVisit(record)) return false;
  if (SILL_MARK_FOR_CLAIMS) return true;
  return result.state === 'confirmed' && isSillFirstPartySigner(record.signed_agent);
}

/**
 * Is this one of Sill's own tools visiting (site scanner, platform
 * detection, install checks …)? A CLAIM, like every logo: the registry
 * matched a Sill tool id (`agent_sill_…`), the UA starts with a Sill tool
 * token, or the visit verified against Sill's first-party signer. Whether
 * Sill proved it is `identityState()`'s job (the Confirmed shield).
 */
export function isSillToolVisit(record: AuditRecord): boolean {
  const id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  if (typeof id === 'string' && id.startsWith('agent_sill_')) return true;
  if (isSillInternalUserAgent(record.user_agent)) return true;
  return record.signature_verified === true && isSillFirstPartySigner(record.signed_agent);
}

/**
 * Which glyph to draw before the name.
 *
 *   - `verification_rejected` → coral ShieldAlert (a real rejected mandate).
 *   - Mismatch → generic Bot in coral, never the impersonated brand.
 *   - One of Sill's own tools → the Sill mark (`isSillToolVisit`, Confirmed
 *     or not — founder 2026-10-13).
 *   - Everything else → `AgentLogo(agent_id ?? inferAgentFromUserAgent(ua))`
 *     (bundled mark → fetched operator icon → monogram, ADR-0242),
 *     whatever the identity state (people fall through to the User glyph
 *     inside ProviderLogo via `visitor_class`).
 */
export function visitLogo(record: AuditRecord, result: IdentityStateResult): VisitLogo {
  if (record.decision === 'verification_rejected') return { kind: 'rejected' };
  if (result.state === 'mismatch') return { kind: 'mismatch' };
  if (showsSillMark(record, result)) return { kind: 'sill' };
  // Only reachable with `SILL_MARK_FOR_CLAIMS = false`: an unconfirmed Sill
  // claim then gets the generic glyph.
  if (isSillToolVisit(record)) return { kind: 'brand', agent_id: null };
  // Founder bug 2026-10-20 — a self-declared UA name that Security has
  // already flagged must not carry the claimed brand's logo. The glyph
  // falls back to the generic Bot (`agent_id: null`), the row still
  // keeps its name as "says it is <token>" via `visitGroupName()`.
  // Verified agents (Confirmed) keep their logo even when flagged.
  if (isUnverifiedFlagged(record)) return { kind: 'brand', agent_id: null };
  const visitorClass = record.identification_match?.visitor_class;
  const serverAgentId = record.identification_match?.agent_id;
  const inferred = visitorClass === 'unknown_agent' && serverAgentId === undefined
    ? inferAgentFromUserAgent(record.user_agent)
    : null;
  return { kind: 'brand', agent_id: serverAgentId ?? inferred };
}

// ---------------------------------------------------------------------------
// Detail header line 2: "{Purpose} · {Operator} · {N} visits … · last seen {t}"
// ---------------------------------------------------------------------------

const SEED_BY_AGENT_ID: ReadonlyMap<string, { category?: AgentRegistryCategory; organization: string }> = new Map(
  BUNDLED_REGISTRY.map((seed) => [seed.agent_id as string, {
    ...(seed.category !== undefined ? { category: seed.category } : {}),
    organization: seed.organization,
  }]),
);

function claimedAgentId(record: AuditRecord): string | undefined {
  const id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  if (id !== undefined) return id;
  if (record.identification_match?.visitor_class === 'unknown_agent') {
    return inferAgentFromUserAgent(record.user_agent) ?? undefined;
  }
  return undefined;
}

export type VisitPurpose =
  | 'AI crawler' | 'AI agent' | 'Search crawler' | 'SEO crawler' | 'Social preview' | 'Monitoring' | 'Sill tool' | 'Bot';

/**
 * The merchant-facing sentence for one of Sill's own tools ("Sill checking
 * your site for AI-agent readiness."), or `null` when the visit is not a
 * Sill tool. Looked up by registry id, else by UA token. Never the staff
 * `purpose` text (ADR numbers, internals; founder 2026-10-14).
 */
export function sillToolPurpose(record: AuditRecord): string | null {
  if (!isSillToolVisit(record)) return null;
  const id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  const ua = record.user_agent ?? '';
  const tool = SILL_INTERNAL_TOOLS.find((t) => t.agent_id === id)
    ?? SILL_INTERNAL_TOOLS.find((t) => ua.startsWith(`${t.token}/`));
  // Merchant-facing sentence only; the staff `purpose` names internals.
  return tool !== undefined ? tool.merchant_purpose : "One of Sill's own tools.";
}

/**
 * Registry category only (never AI text). Fallback `Bot`. Step C: a bot
 * the registry does not know by id is looked up by its product token
 * (`visitCategoryForToken`, the same table the server's purpose filters
 * read), so "AhrefsBot" reads "SEO crawler" and filters under SEO & tools.
 */
export function visitPurpose(record: AuditRecord): VisitPurpose {
  if (isSillToolVisit(record)) return 'Sill tool';
  // Security review L1 (Step C): a registry purpose only when the SERVER
  // matched the agent id; an unmatched UA that merely carries a brand
  // token gets none. Unmatched UAs fall back to the non-registry tools
  // table (AhrefsBot → SEO crawler), the same table the server's chips use.
  const id = record.identification_match?.agent_id ?? record.rejected_agent_id;
  const cat = (id !== undefined ? SEED_BY_AGENT_ID.get(id)?.category : undefined)
    ?? (id === undefined && record.identification_match?.visitor_class !== 'human_likely'
      ? visitCategoryForToken(uaGroupTokenKey(record.user_agent)) ?? undefined
      : undefined);
  switch (cat) {
    case 'ai_crawler': return 'AI crawler';
    case 'ai_assistant': return 'AI agent';
    case 'search': return 'Search crawler';
    case 'seo': return 'SEO crawler';
    case 'social_preview': return 'Social preview';
    case 'monitoring': return 'Monitoring';
    case 'internal': return 'Sill tool';
    default: return 'Bot';
  }
}

/**
 * Registry organisation for the claimed name ("OpenAI", "Huawei").
 * Parenthetical product notes are dropped ("Huawei (Petal Search)" →
 * "Huawei"). `null` when the registry has no entry.
 */
/** Short operator name for a registry agent id ("OpenAI"), or null. */
export function operatorForAgentId(id: string): string | null {
  const org = SEED_BY_AGENT_ID.get(id)?.organization;
  if (org === undefined) return null;
  const short = org.replace(/\s*\(.*\)\s*$/, '').trim();
  return short.length > 0 ? short : null;
}

export function visitOperator(record: AuditRecord): string | null {
  // Sill's own tools: the name ("Sill · Site scanner") and the purpose
  // ("Sill (internal)") already say who; no operator part.
  if (isSillToolVisit(record)) return null;
  const id = claimedAgentId(record);
  return id !== undefined ? operatorForAgentId(id) : null;
}

export interface HeaderLine2Input {
  /** Visits for this agent over the rollup window. `null` = unknown, omitted. */
  readonly visits: number | null;
  /** Window label the count truly covers, e.g. "in 7 days". */
  readonly visitsWindow: string;
}

/**
 * The text parts of header line 2 BEFORE "last seen" (which renders as a
 * `<RelativeTime>` so it keeps its hover timestamp). `null` = no line 2
 * (people, and Mismatch, whose line 2 is the reason sentence instead).
 */
export function headerLine2Parts(
  record: AuditRecord,
  result: IdentityStateResult,
  counts: HeaderLine2Input,
): string[] | null {
  if (result.state === 'person' || result.state === 'mismatch') return null;
  if (record.identification_match?.visitor_class === 'human_likely') return null;
  const parts: string[] = [visitPurpose(record)];
  const op = visitOperator(record);
  if (op !== null) parts.push(op);
  const count = headerCountText(counts);
  if (count !== null) parts.push(count);
  return parts;
}

/** The count part of header line 2, or `null` when unknown / zero. */
export function headerCountText(counts: HeaderLine2Input): string | null {
  if (counts.visits === null || counts.visits <= 0) return null;
  const n = `${counts.visits.toLocaleString('en-US')} visit${counts.visits === 1 ? '' : 's'}`;
  return counts.visitsWindow.length > 0 ? `${n} ${counts.visitsWindow}` : n;
}

/**
 * Tooltip on the header count (security review Step A m3): the rollup is
 * not split by verdict, so next to a Confirmed mark the count must not
 * read as confirmed traffic.
 */
export const HEADER_COUNT_TOOLTIP = "All visits using this name, including ones Sill couldn't confirm.";

// ---------------------------------------------------------------------------
// Step B: one row per agent (grouped list)
// ---------------------------------------------------------------------------

/** The slice of a server visit group these helpers read. */
export interface VisitGroupFacts {
  readonly group_key?: string;
  readonly kind: 'agent' | 'mismatch' | 'people' | 'record';
  readonly visits: number;
  readonly confirmed_records: number;
  readonly top_page_path: string | null;
  readonly representative: AuditRecord;
  readonly probe_hits?: number;
  readonly probe_examples?: readonly string[];
  readonly probe_alert?: boolean;
}

/**
 * "N of M visits came from <Operator>'s verified addresses." Shown next
 * to a Confirmed mark on a grouped row. Founder bug 2026-10-18:
 * "Confirmed in 151 of 1,876 records" read as "151 confirmed, 1,725
 * unknown" right above an impostor warning, which was confusing (and
 * the audit-record vs visit unit distinction was invisible). The new
 * sentence names the operator and makes the unit explicit; the
 * impostor line below the header explains what the remainder is.
 */
export function groupConfirmedNote(g: {
  readonly records: number;
  readonly confirmed_records: number;
  readonly representative?: AuditRecord;
  readonly operator_label?: string;
}): string | null {
  if (g.confirmed_records <= 0) return null;
  const y = Math.max(g.records, g.confirmed_records);
  const x = Math.min(g.confirmed_records, y);
  const operator = resolveOperatorLabel(g) ?? 'the operator';
  return `${x.toLocaleString('en-US')} of ${y.toLocaleString('en-US')} visit${y === 1 ? '' : 's'} came from ${operator}'s verified addresses.`;
}

/**
 * The visible word next to a grouped header's mark. "Confirmed" when
 * every record in the row is Confirmed; otherwise the "N of M visits
 * came from <Operator>'s verified addresses" sentence from
 * {@link groupConfirmedNote}. Founder bug 2026-10-18: see note above.
 */
export function groupConfirmedLabel(g: {
  readonly records: number;
  readonly confirmed_records: number;
  readonly representative?: AuditRecord;
  readonly operator_label?: string;
}): string | null {
  if (g.confirmed_records <= 0) return null;
  if (g.confirmed_records >= g.records) return 'Confirmed';
  const operator = resolveOperatorLabel(g) ?? 'the operator';
  return `${g.confirmed_records.toLocaleString('en-US')} of ${g.records.toLocaleString('en-US')} visits came from ${operator}'s verified addresses`;
}

/** Operator fallback chain: explicit → identity_evidence → agent_id lookup. */
function resolveOperatorLabel(g: {
  readonly representative?: AuditRecord;
  readonly operator_label?: string;
}): string | null {
  if (g.operator_label !== undefined && g.operator_label.length > 0) return g.operator_label;
  const rep = g.representative;
  if (rep === undefined) return null;
  const ev = nonEmpty(rep.identity_evidence?.operator_display);
  if (ev !== undefined) return ev;
  const agent_id = rep.identification_match?.agent_id;
  if (typeof agent_id === 'string') return operatorForAgentId(agent_id);
  return null;
}

/** Visitor-supplied path: bounded so one row can't blow the layout. */
function shortPath(p: string): string {
  return p.length > 80 ? `${p.slice(0, 80)}…` : p;
}

/**
 * Grouped row line 2: "{Operator} · {Purpose} · top page {path}", unknown
 * parts (and the generic "Bot" purpose) omitted; `null` when nothing is
 * known. Mismatch rows never name the operator whose name was used (it is
 * not that operator); people have no line 2. Probing never replaces this
 * line (founder feedback 2026-10-14: the list is a calm roster).
 */
export function groupSecondLine(g: VisitGroupFacts): string | null {
  // One recognised person (ADR-0243 `p.<id>`): "Chrome on Windows".
  if (g.group_key !== undefined && isPersonGroupKey(g.group_key)) return humanRecordDevice(g.representative);
  if (g.kind === 'people' || g.kind === 'record') return null;
  const parts: string[] = [];
  // ADR-0246 — the scanner row names how it hid, not an operator.
  if (g.group_key !== undefined && isScannerGroupKey(g.group_key)) {
    parts.push('Browser user agent');
    if (g.top_page_path !== null && g.top_page_path.length > 0) parts.push(`top page ${shortPath(g.top_page_path)}`);
    return parts.join(' · ');
  }
  if (g.kind === 'agent') {
    const op = visitOperator(g.representative);
    if (op !== null) parts.push(op);
    const purpose = visitPurpose(g.representative);
    if (purpose !== 'Bot') parts.push(purpose);
  }
  if (g.top_page_path !== null && g.top_page_path.length > 0) parts.push(`top page ${shortPath(g.top_page_path)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * The window a grouped count covers, from the list's date filters (UTC
 * days, inclusive). Empty when unfiltered: the count then covers every
 * record the list holds.
 */
export function groupWindowLabel(dateFrom: string, dateTo: string): string {
  if (dateFrom.length > 0 && dateTo.length > 0) {
    return dateFrom === dateTo ? `on ${dateFrom}` : `from ${dateFrom} to ${dateTo}`;
  }
  if (dateFrom.length > 0) return `since ${dateFrom}`;
  if (dateTo.length > 0) return `through ${dateTo}`;
  return '';
}

// ---------------------------------------------------------------------------
// Step C: one row per bot name, probe alert, honest protocol, raw data
// ---------------------------------------------------------------------------

/**
 * A row for ONE recognised person (ADR-0243 `p.<person_id>`), as opposed
 * to the `people` row that stands for every person in range.
 */
export function isPersonGroupKey(group_key: string): boolean {
  return group_key.startsWith('p.');
}

/**
 * ADR-0246 — the "Unknown scanner" row (`a.scan`): requests with a
 * browser-looking user agent for paths no person asks for (`/.env`,
 * `/.git/config`, a webshell). The server never counts them as people.
 */
export const SCANNER_GROUP_KEY = 'a.scan';
export function isScannerGroupKey(group_key: string | undefined): boolean {
  return group_key === SCANNER_GROUP_KEY;
}

/**
 * ADR-0253 — the "Unverified A2A caller" row (`a.a2a`): an A2A
 * SendMessage from a caller the server did NOT match to a registry
 * agent id. Keeps unidentified agent-protocol callers out of the
 * generic `a.noua` row (which otherwise folds bot page visits and
 * `.php` attack probes under "Unidentified visitor"). Verified /
 * registry-matched A2A callers stay in their normal identity row.
 */
export const A2A_UNVERIFIED_GROUP_KEY = 'a.a2a';
export function isA2aUnverifiedGroupKey(group_key: string | undefined): boolean {
  return group_key === A2A_UNVERIFIED_GROUP_KEY;
}

/** The slice of a group `visitGroupName` reads. */
export interface VisitGroupNameFacts {
  readonly group_key: string;
  readonly kind: 'agent' | 'mismatch' | 'people' | 'record';
  readonly representative: AuditRecord;
}

/**
 * The name a grouped row (and its detail header) shows. A bot-token row
 * (`a.tok.<token>`, Step C) groups every version of one client, so a
 * per-version headline ("Unknown bot · python-requests/2.31.0") would
 * name one version only. It keeps the qualifier and drops the version
 * ("Unknown bot · python-requests"): the bare token would read as a known
 * bot (security review L1). "Likely <x>" headlines stay as they are.
 */
export function visitGroupName(group: VisitGroupNameFacts): string {
  // One recognised person reads like one human record: "Visitor from Austin".
  if (isPersonGroupKey(group.group_key)) return humanRecordName(group.representative);
  if (group.kind === 'people') return 'People';
  // ADR-0246 — browser-looking requests for probe paths (`/.env`, …).
  if (isScannerGroupKey(group.group_key)) return 'Unknown scanner';
  // ADR-0253 — A2A SendMessage callers the server did not match to a
  // registry agent id. Honest label: "Unverified" because we can't say
  // who sent it, "A2A" because the protocol itself identifies the row.
  if (isA2aUnverifiedGroupKey(group.group_key)) return 'Unverified A2A caller';
  const result = identityState(group.representative);
  if (group.kind === 'mismatch') return quotedName(result.name);
  // Founder bug 2026-10-20 — a self-declared UA name that Security has
  // flagged must read as a claim, never as the real agent. Overrides
  // the registry/token name below.
  if (isUnverifiedFlagged(group.representative)) {
    const flagged = unverifiedFlaggedName(group.representative);
    if (flagged !== null) return flagged;
  }
  const name = visitListName(recordHeadline(group.representative), result);
  if (group.group_key.startsWith('a.tok.') && (/^Unknown bot\b/.test(name) || name === 'Agent visit observed')) {
    const tok = uaGroupToken(group.representative.user_agent);
    return tok !== null ? `Unknown bot · ${tok}` : name;
  }
  return name;
}

/**
 * Is the site WordPress (for platform-aware probe classes: `/wp-login.php`
 * and `.php` pages are the site's own there)? The dashboard reads the
 * site's detected platform; the server also counts the Sill WordPress
 * plugin and the newest site scan (`siteIsWordPressSql`).
 */
export function siteIsWordPress(site: { readonly detected_platform?: string } | null | undefined): boolean {
  return site?.detected_platform === 'wordpress' || site?.detected_platform === 'woocommerce';
}

/** Probe facts for a row or a single record: hits + example paths. */
export interface ProbeFacts {
  readonly hits: number;
  readonly examples: readonly string[];
}

/**
 * The probe facts behind the "Probing" alert. A group carries the server's
 * count (its NON-Confirmed records and their page hits); a single record
 * counts its own page, unless Sill confirmed it (security review M2).
 * `null` when nothing probed.
 */
export function probeFacts(input: { readonly probe_hits?: number; readonly probe_examples?: readonly string[] } | null, record?: AuditRecord): ProbeFacts | null {
  if (input !== null && typeof input.probe_hits === 'number') {
    return input.probe_hits > 0 ? { hits: input.probe_hits, examples: (input.probe_examples ?? []).map(displayProbePath).filter((p) => p.length > 0).slice(0, 2) } : null;
  }
  if (record === undefined || identityState(record).state === 'confirmed') return null;
  const path = record.page_path;
  if (typeof path === 'string' && path.length > 0 && isProbePath(path)) {
    const shown = displayProbePath(path);
    return { hits: 1, examples: shown.length > 0 ? [shown] : [] };
  }
  return null;
}

/** Max characters of a visitor-supplied probe path we show. */
export const PROBE_PATH_DISPLAY_MAX = 60;

/**
 * A visitor-supplied path made safe to show inside Sill's alert (security
 * review L3): C0/C1 controls, bidi overrides / isolates and zero-width
 * characters are removed, whitespace runs collapse, and the path is cut at
 * 60 characters. Callers render it quoted, in monospace, apart from Sill's
 * own sentence.
 */
export function displayProbePath(p: string): string {
  // eslint-disable-next-line no-control-regex
  const clean = p.replace(/[\t\n\r\f\v]+/g, ' ').replace(/[\u0000-\u001F\u007F-\u009F\u00AD\u061C\u180E\u200B-\u200F\u2028-\u202E\u2060-\u2069\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean.length > PROBE_PATH_DISPLAY_MAX ? `${clean.slice(0, PROBE_PATH_DISPLAY_MAX)}…` : clean;
}

/** Who the probe line speaks about (security review M2). */
export interface ProbeContext {
  /** The row (or visit) also has Sill-Confirmed records. */
  readonly confirmedInRow: boolean;
  /** The name is a registry agent (or an impostor of one). */
  readonly registryName: boolean;
}

/** The probe context for a grouped row, or a single record when `group` is absent. */
export function probeContext(group: { readonly group_key: string; readonly confirmed_records: number } | undefined, record: AuditRecord): ProbeContext {
  if (group !== undefined) {
    return {
      confirmedInRow: group.confirmed_records > 0,
      registryName: /^[am]\.id\./.test(group.group_key) || group.group_key.startsWith('m.'),
    };
  }
  return {
    confirmedInRow: false,
    registryName: record.identification_match?.agent_id !== undefined || identityState(record).state === 'mismatch',
  };
}

/**
 * Meaningful probing (ADR-0244: alerts must be rare and quiet): a sender
 * with no registry name whose probe requests are 5+ and 25%+ of its
 * visits, or a registry-named row whose unverified visits made 3+
 * requests to universal probe classes (`isMeaningfulProbing`,
 * `@sill/audit`). The server's `probe_alert` wins; older APIs fall back to
 * the same rule here. Drives the list's Probing pill and the detail
 * header's quiet line.
 */
export function probeAlert(g: { readonly group_key: string; readonly visits: number; readonly probe_hits?: number; readonly probe_hits_universal?: number; readonly probe_alert?: boolean }): boolean {
  if (typeof g.probe_alert === 'boolean') return g.probe_alert;
  return isMeaningfulProbing(g);
}

/**
 * The detail header line for an alerting registry-named row (ADR-0244):
 * someone is using the name. Neutral, no WAF hint: blocking the name
 * could block the real agent. `blockHint` only when the registry knows
 * how to verify the operator's traffic.
 */
export interface ImpostorProbeLine {
  /**
   * "Someone using the {Name} name requested secret files" (" like
   * <example>" follows when there is one). "sensitive paths" when the
   * example shown is not a secret file (`/api/console/…`, a webshell).
   */
  readonly lead: string;
  /** The one example to show (a secret-file path first), or null. */
  readonly example: string | null;
  /** ". The real {Name} doesn't do this; these visits weren't verified as {Operator}." */
  readonly tail: string;
  readonly blockHint: string | null;
}

/**
 * Registry agent ids whose operator publishes an IP list or reverse-DNS
 * names (security review m3): a merchant's WAF can allow only that
 * verified traffic. Key-only / Web Bot Auth-only agents are left out (a
 * WAF usually cannot check those), as are DB-only agents (fails safe).
 */
const VERIFIABLE_AGENT_IDS: ReadonlySet<string> = new Set(
  BUNDLED_REGISTRY
    .filter((seed) => (seed.ip_ranges?.length ?? 0) > 0 || (seed.rdns_suffixes?.length ?? 0) > 0)
    .map((seed) => seed.agent_id as string),
);

/** Can a WAF allow only this agent's verified traffic (published IP list or rDNS)? */
export function registryCanVerify(agent_id: string): boolean {
  return VERIFIABLE_AGENT_IDS.has(agent_id);
}

/** `null` unless the row is a registry-named row (not a Sill tool) with the alert. */
export function impostorProbeLine(
  group: { readonly group_key: string; readonly visits: number; readonly probe_hits?: number; readonly probe_hits_universal?: number; readonly probe_alert?: boolean },
  name: string,
  examples: readonly string[] = [],
): ImpostorProbeLine | null {
  if (!isNamedSenderGroupKey(group.group_key) || !probeAlert(group)) return null;
  // The row's own agent id (security review n1), never a record's claim.
  const agent_id = group.group_key.slice('a.id.'.length);
  const operator = operatorForAgentId(agent_id);
  const verifiedAs = operator !== null ? ` as ${operator}` : '';
  const shown = examples.map(displayProbePath).filter((p) => p.length > 0);
  const secret = shown.find((p) => probeKindOf(p, { wordpress: true }) === 'secret_file');
  const example = secret ?? shown[0] ?? null;
  const what = example === null || secret !== undefined ? 'secret files' : 'sensitive paths';
  return {
    lead: `Someone using the ${name} name requested ${what}`,
    example,
    tail: `. The real ${name} doesn't do this; these visits weren't verified${verifiedAs}.`,
    // Founder bug 2026-10-18: point the merchant at the panel's
    // Block ▾ (which blocks ONLY the impostor's addresses — Sill
    // screens out verified-crawler addresses on blocklist writes) and
    // tell them the real crawler keeps working.
    blockHint: operator !== null && registryCanVerify(agent_id)
      ? `Block the impostor's addresses — the real ${name} keeps working.`
      : null,
  };
}

/** "N requests to sensitive paths": it counts requests, not paths. */
export function probeRequestsLead(hits: number): string {
  return `${hits.toLocaleString('en-US')} request${hits === 1 ? '' : 's'} to sensitive paths`;
}

/**
 * The quiet note on a registry-named row whose visits requested probe
 * paths: those come from other senders borrowing the name.
 */
export function probeBorrowedNameNote(hits: number, name: string): string {
  const n = hits.toLocaleString('en-US');
  return `${n} visit${hits === 1 ? '' : 's'} using this name requested sensitive paths. These usually come from other senders borrowing the name, not from ${name}.`;
}

/**
 * The "block it" hint is only for senders that use no registry name and
 * have no Confirmed visits: blocking a registry name could block the real
 * crawler (security review M2). Mismatch rows have their own firewall
 * callout.
 */
export function probeShowsBlockHint(ctx: ProbeContext): boolean {
  return !ctx.confirmedInRow && !ctx.registryName;
}

/** The quiet hint under the probe line. Founder 2026-10-19: Sill blocks
 *  now (the Block button above), so the old "does not block" advice went. */
export const PROBE_BLOCK_HINT = 'Use Block above to stop this sender. Blocks apply while blocking is on in Security › Blocking.';

/**
 * "Protocol" in the detail panel: how the agent talked to the site. A page
 * visit seen by the script tag, the Worker or the plugin is plain web
 * traffic; MCP / A2A / AP2 / UCP only when the agent used Sill's agent
 * endpoints with that protocol. Where Sill saw it is `seenByLabel`.
 *
 * ADR-0253 §A — the vocabulary is the canonical `INBOUND_PROTOCOLS`;
 * legacy `'embed'` and the pre-fold raw column also render correctly.
 * The dashboard reads this label off the server-derived value; it never
 * re-derives.
 */
export function protocolLabel(p: string): string {
  switch (p) {
    case 'embed': return 'Web (HTTP)';
    case 'web': return 'Web (HTTP)';
    case 'mcp': return 'MCP';
    case 'a2a': return 'A2A';
    case 'ap2': return 'AP2';
    case 'ucp': return 'UCP';
    case 'acp': return 'ACP';
    case 'pap': return 'PAP';
    // Founder bug 2026-10-25: "Unknown" read as an assertion; "Not
    // recorded" matches the dashboard's rule of never inventing values.
    case 'unknown': return 'Not recorded';
    case '': return 'Not recorded';
    default: return p.toUpperCase();
  }
}

/**
 * ADR-0253 §A — the derived protocol a record's Protocol row renders.
 * Prefers the server's `derived_protocol` (always one of
 * `INBOUND_PROTOCOLS`); falls back to the raw column for pre-fix
 * responses; collapses to `'unknown'` when both are absent. The
 * dashboard NEVER re-derives from JSONB client-side — this is the
 * entire derivation path.
 */
export function recordDerivedProtocol(
  record: Pick<AuditRecord, 'derived_protocol' | 'inbound_protocol'>,
): string {
  const d = record.derived_protocol;
  if (typeof d === 'string' && d.length > 0) return d;
  const raw = record.inbound_protocol;
  if (typeof raw === 'string' && raw.length > 0) return raw;
  return 'unknown';
}

/**
 * "Seen by": which part of the merchant's install (or Sill's endpoint)
 * recorded the visit. `null` when the record does not say.
 */
export function seenByLabel(record: Pick<AuditRecord, 'via' | 'inbound_protocol'>): string | null {
  if (record.via === 'worker') return 'Cloudflare Worker';
  if (record.via === 'plugin') return 'WordPress plugin';
  if (record.via === 'server') return 'Server-side install';
  // ADR-0259 amendment — Sill's own welcome visit on a script-tag-only
  // site, recorded by Sill, not by the merchant's install.
  if (record.via === 'sill') return 'Recorded by Sill, not seen by your install';
  if (record.inbound_protocol === 'embed') return "Your site's script tag";
  if (record.inbound_protocol !== undefined) return "Sill's agent endpoint";
  return null;
}

/** One protocol in plain words, then the general rule (PROTOCOL_TOOLTIP). */
export function protocolTooltip(p: string): string {
  const one: Record<string, string> = {
    web: 'Web (HTTP): an ordinary page request to your site.',
    embed: 'Web (HTTP): an ordinary page request to your site.',
    mcp: 'MCP (Model Context Protocol): the agent called your store\'s tools through Sill\'s agent endpoint.',
    a2a: 'A2A (Agent2Agent): the agent sent a task to your store through Sill\'s agent endpoint.',
    ap2: 'AP2 (Agent Payments Protocol): the agent sent a signed payment mandate through Sill\'s agent endpoint.',
    ucp: 'UCP (Universal Commerce Protocol): a shopping platform ran a checkout against your store through Sill\'s UCP endpoint.',
    acp: 'ACP (Agentic Commerce Protocol): the agent paid with a Stripe shared payment token through Sill\'s agent endpoint.',
    pap: 'PAP (Personal Agent Protocol): the agent spoke to your site inside a bound session.',
    unknown: 'Sill could not identify how this request reached your site.',
  };
  return one[p] ?? PROTOCOL_TOOLTIP;
}

export const PROTOCOL_TOOLTIP =
  "How the agent talked to your site. Web (HTTP) is an ordinary page request. MCP, A2A, AP2 and UCP appear only when the agent used Sill's agent endpoints with that protocol.";
export const SEEN_BY_TOOLTIP =
  "Which part of your install recorded this visit: your site's script tag, your Cloudflare Worker, your WordPress plugin or a server-side install, or Sill's agent endpoint.";

/**
 * Raw data → "Identity check": the server's verdict in one plain sentence,
 * e.g. "Signature valid (OpenAI)". `null` when nothing was recorded (the
 * default state has no row).
 */
export function identityCheckSentence(record: AuditRecord, result: IdentityStateResult): string | null {
  if (result.state === 'mismatch') {
    const why = mismatchReason(record, result);
    return why !== null ? `Failed. ${why}` : 'Failed.';
  }
  const op = nonEmpty(result.operator) ?? nonEmpty(record.identity_evidence?.operator_display);
  const who = op !== undefined ? ` (${op})` : '';
  switch (record.identity_verification) {
    case 'signature': return `Signature valid${who}`;
    case 'published_ip': return op !== undefined ? `Address is on ${op}'s published list` : 'Address is on the operator\'s published list';
    case 'rdns': return op !== undefined ? `Address traces back to ${op}` : 'Address traces back to the operator';
    case 'unverified': return "Checked; Sill couldn't confirm who sent it";
    case 'failed': return 'Check failed';
    default: return null;
  }
}
